import React from 'react';
import { vmsService } from '../../../services/vmsService';

interface DocumentsSectionProps {
  vendorDocs: any[];
  setVendorDocs: React.Dispatch<React.SetStateAction<any[]>>;
  docTypes: any[];
  setDocTypes: React.Dispatch<React.SetStateAction<any[]>>;
  initialDataId?: number;
}

export const DocumentsSection: React.FC<DocumentsSectionProps> = ({ vendorDocs, setVendorDocs, docTypes, setDocTypes, initialDataId }) => {
  const getDropdownDocTypes = () => {
    const initialNames = ['Agreement', 'NDA', 'LOE Letter', 'Engagement Letter'];
    const merged = [...docTypes];
    initialNames.forEach(name => {
      if (!merged.some(dt => dt.name.toLowerCase() === name.toLowerCase())) {
        merged.push({ id: `INITIAL_${name}`, name });
      }
    });
    return merged;
  };

  const handleRemoveDoc = async (index: number) => {
    const doc = vendorDocs[index];
    if (doc.id && initialDataId) {
      if (window.confirm(`Are you sure you want to delete this document from the backend?`)) {
        try {
          await vmsService.deleteVendorDocDetail(initialDataId, doc.id);
          setVendorDocs(prev => prev.filter((_, i) => i !== index));
        } catch (err) {
          alert('Failed to delete document detail from backend.');
        }
      }
    } else {
      setVendorDocs(prev => prev.filter((_, i) => i !== index));
    }
  };

  return (
    <>
      <h3 style={{ borderBottom: '1px solid #eee', paddingBottom: '0.5rem', marginBottom: '1.5rem', marginTop: '2.5rem', color: 'var(--text-main)' }}>2. Documents/Attachments</h3>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
        {vendorDocs.map((doc, idx) => (
          <div key={idx} className="form-row" style={{ alignItems: 'flex-start', border: '1px solid #eee', padding: '1rem', borderRadius: '8px', backgroundColor: '#fafafa' }}>
            <div className="filter-group" style={{ flex: 1 }}>
              <label>Document Type *</label>
              {!doc.isNewType ? (
                <select 
                  value={doc.doc_type_id || ''} 
                  onChange={e => {
                    const updated = [...vendorDocs];
                    if (e.target.value === 'NEW') {
                      updated[idx].isNewType = true;
                    } else {
                      updated[idx].doc_type_id = e.target.value;
                    }
                    setVendorDocs(updated);
                  }}
                  required
                  disabled={!!doc.id}
                >
                  <option value="">Select Document Type</option>
                  {getDropdownDocTypes().map(dt => (
                    <option key={dt.id} value={dt.id}>{dt.name}</option>
                  ))}
                  <option value="NEW">+ Create New Type</option>
                </select>
              ) : (
                <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                  <input 
                    type="text" 
                    placeholder="New Document Type Name" 
                    required 
                    value={doc.newTypeName} 
                    onChange={e => {
                      const updated = [...vendorDocs];
                      updated[idx].newTypeName = e.target.value;
                      setVendorDocs(updated);
                    }} 
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                    <button 
                      type="button" 
                      onClick={() => {
                        const updated = [...vendorDocs];
                        updated[idx].isNewType = false;
                        updated[idx].newTypeName = '';
                        setVendorDocs(updated);
                      }} 
                      style={{ fontSize: '0.85rem', color: 'var(--text-light)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      Cancel
                    </button>
                    <button 
                      type="button" 
                      onClick={async (e) => {
                        e.preventDefault();
                        if (!doc.newTypeName.trim()) return;
                        try {
                          const newType = await vmsService.createDocType({ name: doc.newTypeName, description: `Vendor ${doc.newTypeName} Document` });
                          const data = await vmsService.getDocTypes();
                          setDocTypes(data.results || data);
                          
                          const updated = [...vendorDocs];
                          updated[idx].doc_type_id = String(newType.id);
                          updated[idx].isNewType = false;
                          updated[idx].newTypeName = '';
                          setVendorDocs(updated);
                        } catch (err) {
                          alert('Failed to save new document type');
                        }
                      }} 
                      style={{ fontSize: '0.85rem', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}
                    >
                      Save
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="filter-group" style={{ flex: 1 }}>
              <label>File {doc.id ? '(Optional - Upload to replace)' : '*'}</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <input 
                  type="file" 
                  onChange={e => {
                    const file = e.target.files?.[0] || null;
                    const updated = [...vendorDocs];
                    updated[idx].file = file;
                    setVendorDocs(updated);
                  }}
                  required={!doc.id}
                />
                {doc.id && (
                  <button
                    type="button"
                    className="secondary-btn sm-btn"
                    style={{ alignSelf: 'flex-start' }}
                    onClick={async () => {
                      try {
                        const res = await vmsService.downloadCurrentVendorDoc(initialDataId!, doc.id!);
                        if (res?.url) window.open(res.url, '_blank');
                      } catch (err) {
                        alert('Failed to download document');
                      }
                    }}
                  >
                    View Current File
                  </button>
                )}
              </div>
            </div>

            <button 
              type="button" 
              onClick={() => handleRemoveDoc(idx)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', marginLeft: 'auto', color: '#ef4444', marginBottom: '8px' }}
              title="Remove Document"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
            </button>
          </div>
        ))}

        <button 
          type="button" 
          className="secondary-btn" 
          onClick={() => setVendorDocs(prev => [...prev, { doc_type_id: '', isNewType: false, newTypeName: '', file: null }])}
          style={{ alignSelf: 'flex-start' }}
        >
          + Add Document
        </button>
      </div>
    </>
  );
};
